import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IProfessionalInvitation } from '../../../_interfaces/IProfessionalInvitation';
import { CustomersService } from '../../../_services/customers/customers.service';
import { readAdminCustomerRouteParam, readRouteParam } from '../../../_utils/route-param.utils';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../../_services/session/session.service';

@Component({
  selector: 'app-admin-user-invitations',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, ButtonModule, TableModule],
  templateUrl: './admin-user-invitations.component.html',
  styleUrl: './admin-user-invitations.component.scss',
})
export class AdminUserInvitationsComponent implements OnInit {
  public customersId = 0;
  public customerRouteParam = '';
  public loading = false;
  public invitations: IProfessionalInvitation[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly customersService: CustomersService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly session: SessionService,
  ) {}

  ngOnInit(): void {
    this.customersId =
      readRouteParam(this.route, 'customers_id') ||
      Number(this.session.getEffectiveCustomerId() || 0) ||
      Number(this.session.getCustomer()?.id || 0);
    this.customerRouteParam =
      readAdminCustomerRouteParam(this.route, this.customersId) || `${this.customersId}`;
    this.loadInvitations();
  }

  get createUserLink(): any[] {
    return ['/administration', this.customerRouteParam, 'users', 'new'];
  }

  loadInvitations(): void {
    if (!this.customersId) {
      this.invitations = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    this.customersService.getUserInvitations(this.customersId).subscribe({
      next: (invitations) => {
        this.invitations = invitations || [];
        this.loading = false;
      },
      error: () => {
        this.invitations = [];
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant('ADMIN.USERS.INVITATION.PENDING_LOAD_ERROR'),
        });
      },
    });
  }

  resendInvitation(invitation: IProfessionalInvitation): void {
    this.customersService.resendUserInvitation(this.customersId, invitation.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SAVE'),
          detail: this.translate.instant('ADMIN.USERS.INVITATION.RESENT', {
            email: invitation.email,
          }),
        });
        this.loadInvitations();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant('MESSAGES.ERRORS.CREATE.USERS.DETAIL'),
        });
      },
    });
  }

  cancelInvitation(invitation: IProfessionalInvitation): void {
    if (!window.confirm(this.translate.instant('ADMIN.USERS.INVITATION.CANCEL_CONFIRM'))) {
      return;
    }
    this.customersService.cancelUserInvitation(this.customersId, invitation.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SAVE'),
          detail: this.translate.instant('ADMIN.USERS.INVITATION.CANCELLED', {
            email: invitation.email,
          }),
        });
        this.loadInvitations();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant('MESSAGES.ERRORS.CREATE.USERS.DETAIL'),
        });
      },
    });
  }
}
